import Feature from 'ol/Feature';
import * as olExtent from 'ol/extent';
import { transformExtent } from 'ol/proj';
import { Modal } from 'bootstrap';
import { IUserGpxRouteSidebarItem } from './GpxRouteApi';

/**
 * Normalise a raw hex colour string: trim whitespace, ensure leading #, uppercase.
 * Validates it as a 6-digit hex and falls back to the default on anything else —
 * this is the single guard for colours interpolated (unescaped) into HTML/SVG
 * attributes and inline styles downstream, so a malformed value can't break out.
 */
export function normaliseColour(colour: string, defaultColour: string): string {
    if (!colour) {
        return defaultColour;
    }
    let normalised = colour.trim();
    if (!normalised.startsWith('#')) {
        normalised = '#' + normalised;
    }
    normalised = normalised.toUpperCase();
    return /^#[0-9A-F]{6}$/.test(normalised) ? normalised : defaultColour;
}

/** Strip the .gpx extension from a filename and return a trimmed display name. */
export function createDisplayNameFromFile(fileName: string): string {
    const withoutExtension = fileName.replace(/\.gpx$/i, '');
    return withoutExtension.trim() || 'GPX Route';
}

/** HTML-escape a string for safe insertion into element text / attribute content. */
export function escapeHtml(value?: string | null): string {
    if (!value) {
        return '';
    }
    const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
    };
    return value.replace(/[&<>"']/g, (ch: string) => entities[ch] ?? ch);
}

/** Escape a value for use inside an HTML attribute (newlines become &#10;). */
export function escapeAttribute(value?: string | null): string {
    if (!value) {
        return '';
    }
    return escapeHtml(value).replace(/\r?\n/g, '&#10;');
}

/** Resolve a potentially relative share URL against window.location.origin. */
export function normaliseShareUrl(value: string): string {
    try {
        return new URL(value, window.location.origin).toString();
    } catch {
        return value;
    }
}

/** Compute the bounding extent of a set of OL features (EPSG:3857). */
export function calculateExtent(features: Feature[]): olExtent.Extent {
    const extent = olExtent.createEmpty();
    features.forEach(f => olExtent.extend(extent, f.getGeometry().getExtent()));
    return extent;
}

/**
 * Convert the EPSG:4326 bounding box on a sidebar item into an EPSG:3857
 * OL extent. Returns null if any coordinate is missing.
 */
export function extentFrom4326SidebarItem(item: IUserGpxRouteSidebarItem): olExtent.Extent | null {
    if (item.extentMinX == null || item.extentMinY == null ||
        item.extentMaxX == null || item.extentMaxY == null) {
        return null;
    }
    return transformExtent(
        [item.extentMinX, item.extentMinY, item.extentMaxX, item.extentMaxY],
        'EPSG:4326',
        'EPSG:3857'
    );
}

/**
 * Extract a human-readable message from a fetch error thrown by GpxRouteApi.
 * Falls back to `fallback` if the error doesn't contain a structured message.
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) {
        const message = error.message;
        const marker = '): ';
        const markerIndex = message.indexOf(marker);
        if (markerIndex >= 0) {
            const detail = message.substring(markerIndex + marker.length).trim();
            if (detail) {
                try {
                    const parsed = JSON.parse(detail) as { message?: string };
                    if (parsed?.message) {
                        return parsed.message;
                    }
                } catch {
                    return detail;
                }
            }
        }
        return message;
    }
    return fallback;
}

/**
 * Ensure a Bootstrap modal element is a direct child of <body> (required for
 * correct stacking / backdrop behaviour), then return the element and instance.
 */
export function getModalById(elementId: string): { element: HTMLElement; instance: Modal } {
    const element = document.getElementById(elementId) as HTMLElement;
    if (element.parentElement !== document.body) {
        document.body.appendChild(element);
    }
    return {
        element,
        instance: Modal.getOrCreateInstance(element)
    };
}

/** Copy text to the clipboard using the Clipboard API with an execCommand fallback. */
export async function copyTextToClipboard(value: string): Promise<boolean> {
    if (!value) {
        return false;
    }
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return true;
        }
    } catch (error) {
        console.warn('navigator.clipboard.writeText failed', error);
    }
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    let copied = false;
    try {
        copied = document.execCommand('copy');
    } catch (err) {
        console.warn('document.execCommand copy failed', err);
    } finally {
        document.body.removeChild(textarea);
    }
    return copied;
}
